import React from "react";
import styled, { css } from "styled-components";
import { useInView } from "react-hook-inview";
import { animated, SpringValue, interpolate } from "react-spring";
import { mapConfig } from "../../utils/map-utils/map-config";
import { clamp } from "lodash";
import { Theme } from "../../utils/theme";
import Icon from "../shared/Icon";
import Text from "../shared/Text";
import { PanelContext } from "../../contexts/PanelContext";
import { useOutsideClick } from "../../hooks/useOutsideClick";

type PinType = "cluster" | "point";
type ContentType = "story" | "event" | "site";
interface IProps {
  id: string;
  x: number;
  y: number;
  qty?: number;
  title?: string;
  contentType: ContentType;
  pinType: PinType;
  currentZoom: SpringValue<number>;
  zoom: (amount: number, screenX?: number, screenY?: number) => void;
}
const Marker: React.FC<IProps> = React.memo((props) => {
  const [, setPanel] = React.useContext(PanelContext);
  const [contentPreview, setContentPreview] = React.useState(false);
  const [ref, isVisible] = useInView({
    threshold: 0,
  });
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const { outsideClick, watchOutsideClicks } = useOutsideClick(wrapperRef);

  React.useEffect(() => {
    if (outsideClick) {
      setContentPreview(false);
      watchOutsideClicks(false);
    }
  }, [outsideClick, watchOutsideClicks]);
  /**
   * Changes the scale so that the markers
   * appear to be thes same size even when
   * the parent container is zoomed in.
   */
  const getScale = React.useCallback((scale: number) => {
    const maxZoom = mapConfig.zoom.max;
    const minZoom = mapConfig.zoom.min;

    const zoomScale = maxZoom - minZoom;
    const currentScale = scale - minZoom;

    const percentZoom = currentScale / zoomScale;
    const throttled = Math.pow(percentZoom, 0.6613);
    return clamp(1 / throttled, 1, 4);
  }, []);

  const getColor = React.useCallback(
    (contentType: ContentType): keyof Theme["colors"] => {
      switch (contentType) {
        case "event":
          return "darkGreen";
        case "site":
          return "darkRed";
        case "story":
        default:
          return "darkBlue";
      }
    },
    []
  );

  const handleClickMarker = React.useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      if (props.pinType === "cluster") {
        props.zoom(0.5, e.screenX, e.screenY);
      } else {
        watchOutsideClicks(true);
        setContentPreview(true);
      }
    },
    [props, watchOutsideClicks]
  );

  const handleClickContentPreview = React.useCallback(() => {
    setPanel({
      open: true,
      data: {
        id: props.id,
        type: "poi",
      },
    });
  }, [props.id, setPanel]);

  return (
    <BaseMarker
      id={`${props.pinType}-${props.id}`}
      onClick={handleClickMarker}
      ref={ref}
      className={isVisible ? "active" : ""}
      pintype={props.pinType}
      contenttype={props.contentType}
      style={{
        transform: interpolate(
          [props.currentZoom],
          (scale) => `translate(-50%, -50%) scale(${getScale(scale)}) `
        ),
        top: `${mapConfig.height * (props.y / 100) - mapConfig.height / 2}px`,
        left: `${mapConfig.width * (props.x / 100) - mapConfig.width / 2}px`,
      }}
    >
      <ContentPreview
        ref={wrapperRef}
        className={contentPreview ? "active" : "hidden"}
        onClick={handleClickContentPreview}
      >
        <PreviewInner>
          <Text as="h2">{props.title}</Text>
          <Icon
            icon="arrowLongRight"
            size={30}
            fillColor={getColor(props.contentType)}
          />
        </PreviewInner>
      </ContentPreview>
      {props.pinType === "cluster" ? (
        <MarkerInner>{props.qty}</MarkerInner>
      ) : (
          <MarkerInner>
            <Icon
              icon={props.contentType}
              size={45}
              fillColor={getColor(props.contentType)}
            />
          </MarkerInner>
        )}
    </BaseMarker>
  );
});

const ContentPreview = styled.div`
  position: absolute;
  background: white;
  z-index: 10;
  top: -2rem;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  width: 250px;

  transform: translate(0px, 0%);
  opacity: 0;
  pointer-events: none;

  transition: all ease-in-out 0.2s;

  &.active {
    pointer-events: all;
    opacity: 1;
    transform: translate(0px, -100%);
  }

  &:before {
    content: "";
    position: absolute;
    bottom: -14px;
    border-top: 15px white solid;
    border-right: 15px transparent solid;
    border-left: 15px transparent solid;
    left: 50%;
    transform: translate(-50%, 0);
  }

  &:after {
    content: "";
    position: absolute;
    transform: translate(-50%) rotate(45deg);
    width: 22px;
    height: 22px;
    bottom: -10px;
    z-index: -1;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
    left: 50%;
  }

  &:hover {
    svg {
      transform: translateX(10px);
    }
  }
`;

const PreviewInner = styled.div`
  width: calc(100% - 2rem);
  height: calc(100% - 2rem);
  background: white;
  border-radius: 4px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  h2 {
    margin: 0;
  }
  svg {
    transition: all ease-in-out 0.2s;
    margin-top: auto;
  }
`;

const MarkerInner = styled.div`
  font-weight: 600;
  isolation: isolate;
  mix-blend-mode: screen;

  color: white;
`;

/**
 * Pin / Cluster styles
 */
const PinMarkerStyles = (theme: Theme) => css`
  border-width: 12px;
  border-style: solid;
  background: none;
  height: 60px;
  width: 60px;
`;

const ClusterMarkerStyles = (theme: Theme) => css`
  height: 80px;
  width: 80px;
  font-size: 2.5rem;
`;

const choosePinType = (type: PinType, theme: Theme) => {
  switch (type) {
    default:
    case "point":
      return PinMarkerStyles(theme);

    case "cluster":
      return ClusterMarkerStyles(theme);
  }
};

/**
 * Story / Event / Site markers
 */
const StoryStyles = (theme: Theme) => css`
  border-color: ${theme.poi.story};
  background-color: ${theme.poi.story};
`;

const EventStyles = (theme: Theme) => css`
  border-color: ${theme.poi.event};
  background-color: ${theme.poi.event};
`;

const SiteStyles = (theme: Theme) => css`
  border-color: ${theme.poi.site};
  background-color: ${theme.poi.site};
`;

const chooseContentType = (type: ContentType, theme: Theme) => {
  switch (type) {
    default:
    case "story":
      return StoryStyles(theme);
    case "event":
      return EventStyles(theme);
    case "site":
      return SiteStyles(theme);
  }
};
/**
 * Base marker style
 */
const BaseMarker = styled(animated.div as any) <{
  pintype: PinType;
  contenttype: ContentType;
}>`
  ${({ pintype, contenttype, theme }) => css`
    position: absolute;
    transform: translate(-50%, -50%);
    user-select: none;
    z-index: 20;
    cursor: pointer;

    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 100%;
    visibility: hidden;

    &.active {
      visibility: visible;
    }

    ${chooseContentType(contenttype, theme)};
    ${choosePinType(pintype, theme)};
  `}
`;

export default Marker;
